import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { path } from 'ramda';
import { fmtzDate, isBillingDev } from '../../common/ambient';
import {
  subStatusDisplay,
  displayPriceForSubscription,
  subIsPlus,
  billingDeferSubscription,
  USER_SUBSCRIPTIONS,
  CAM_TIERS,
  CELL_SUBSCRIPTIONS,
  HEAT_STRESS_SUBSCRIPTIONS,
  isCameraSubscription,
  isCellularSubscription,
  isCellularDevice
} from './';
import './AdminUserSubscriptions.less';

function AdminUserSubscriptions({
  user,
  subscriptions,
  devices,
  onRefreshUser
}) {
  const [extendSubscription, setExtendSubscription] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [extendingSubscription, setExtendingSubscription] = useState(false);

  // Add subscription form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState('');
  const [selectedDevice, setSelectedDevice] = useState('');
  const [addDays, setAddDays] = useState(30);
  const [addingSubscription, setAddingSubscription] = useState(false);

  // Global success message for both add and extend operations
  const [globalSuccessMessage, setGlobalSuccessMessage] = useState(null);

  const userSubs = subscriptions || path(['payments', 'subscriptions'], user) || [];
  const customerId = path(['payments', 'customerId'], user);
  
  if (userSubs.length === 0 && !customerId) return null;

  const handleExtendInputChange = (e) => {
    setExtendDays(e.target.value);
  };
  
  const toggleExtendSubscription = (sub) => {
    setExtendSubscription(prevSub => prevSub === sub ? null : sub);
  };
  
  const submitExtendSubscription = async () => {
    if (!extendSubscription || !extendDays || extendDays <= 0) return;
    
    setExtendingSubscription(true);
    
    try {
      const result = await billingDeferSubscription(
        user.email,
        parseInt(extendDays, 10),
        extendSubscription.productName,
        requiresDevice(extendSubscription.productName) ? extendSubscription.macAddress : undefined
      );
      
      if (result.subscriptionId || (result.message && result.message.toLowerCase().includes('successfully'))) {
        // Set to "processing" state with successful message
        setExtendingSubscription('success');
        setGlobalSuccessMessage(`${extendSubscription.nickname || extendSubscription.productName} extended by ${extendDays} days!`);

        // Wait a few seconds for webhooks to process before refreshing
        setTimeout(() => {
          if (onRefreshUser) onRefreshUser();
          setExtendSubscription(null);
          setExtendDays(30);
          setExtendingSubscription(false);
          setGlobalSuccessMessage(null);
        }, 3000); // 3 second delay
      } else {
        // Show appropriate error message
        const errorMessage = result.message || 'Failed to extend subscription. Please try again.';
        alert(errorMessage);
        setExtendingSubscription(false);
      }
    } catch (error) {
      console.error('Error extending subscription:', error);
      alert('An error occurred while extending the subscription.');
      setExtendingSubscription(false);
    }
  };

  // Helper: Check if subscription requires a device
  const requiresDevice = (productName) => {
    return isCameraSubscription(productName) || isCellularSubscription(productName);
  };

  // Helper: Check if device can have camera subscriptions
  const canHaveCameraSubscription = (device) => {
    return device && device.lastData;
  };

  // Helper: Check if user already has this subscription
  const userHasSubscription = (productName) => {
    if (requiresDevice(productName)) {
      // For device-level subscriptions, just check if any device has it
      // We'll filter devices in the device dropdown
      return false;
    }
    // For account-level subscriptions, check if user has it
    return userSubs.some(sub => sub.productName === productName && (!sub.macAddress || sub.macAddress === 'Account'));
  };

  // Helper: Check if device already has the selected subscription
  const deviceHasSubscription = (macAddress, productName) => {
    return userSubs.some(sub => sub.macAddress === macAddress && sub.productName === productName);
  };

  // Helper: Get display name with billing term
  const getSubscriptionDisplayName = (sub) => {
    const baseName = sub.displayName || sub.label || sub.productName;
    const term = sub.period === 'year' ? 'Annual' : sub.period === 'month' ? 'Monthly' : '';

    // If the name already includes the term, don't add it again
    if (/monthly|annual|yearly/i.test(baseName)) {
      return baseName;
    }

    return term ? `${baseName} (${term})` : baseName;
  };

  // Helper: Get all available subscriptions grouped by type
  const getAvailableSubscriptions = () => {
    const groups = [];

    // AWN+ subscriptions (filter out SMS)
    const awnPlusSubs = USER_SUBSCRIPTIONS
      .filter(sub => !userHasSubscription(sub.productName))
      .filter(sub => sub.productName !== 'awn_sms_notifications');

    if (awnPlusSubs.length > 0) {
      groups.push({
        label: 'AWN+ Subscriptions',
        options: awnPlusSubs
      });
    }

    // Camera subscriptions - only show if user has camera-capable devices
    const hasCameraCapableDevices = (devices || []).some(device => canHaveCameraSubscription(device));
    const cameraSubs = CAM_TIERS
      .filter(tier => tier.productName) // Exclude standard (empty productName)
      .filter(tier => !userHasSubscription(tier.productName));

    if (cameraSubs.length > 0 && hasCameraCapableDevices) {
      groups.push({
        label: 'Camera Subscriptions',
        options: cameraSubs
      });
    }

    // Cellular subscriptions - only show if user has cellular devices
    const hasCellularDevices = (devices || []).some(device => isCellularDevice(device));
    const cellularSubs = CELL_SUBSCRIPTIONS
      .filter(sub => !userHasSubscription(sub.productName));

    if (cellularSubs.length > 0 && hasCellularDevices) {
      groups.push({
        label: 'Cellular Subscriptions',
        options: cellularSubs
      });
    }

    // Heat Stress subscriptions
    const heatStressSubs = HEAT_STRESS_SUBSCRIPTIONS
      .filter(sub => !userHasSubscription(sub.productName));

    if (heatStressSubs.length > 0) {
      groups.push({
        label: 'Heat Stress Subscriptions',
        options: heatStressSubs
      });
    }

    return groups;
  };

  // Handle add subscription form submission
  const submitAddSubscription = async () => {
    if (!selectedSubscription || !addDays || addDays <= 0) return;

    // Check if device is required and selected
    if (requiresDevice(selectedSubscription) && !selectedDevice) {
      alert('Please select a device for this subscription.');
      return;
    }

    setAddingSubscription(true);

    try {
      const result = await billingDeferSubscription(
        user.email,
        parseInt(addDays, 10),
        selectedSubscription,
        requiresDevice(selectedSubscription) ? selectedDevice : undefined
      );

      if (result.subscriptionId || (result.message && result.message.toLowerCase().includes('successfully'))) {
        setAddingSubscription('success');
        setGlobalSuccessMessage(`Subscription added successfully for ${addDays} days!`);

        setTimeout(() => {
          if (onRefreshUser) onRefreshUser();
          setShowAddForm(false);
          setSelectedSubscription('');
          setSelectedDevice('');
          setAddDays(30);
          setAddingSubscription(false);
          setGlobalSuccessMessage(null);
        }, 3000);
      } else {
        const errorMessage = result.message || 'Failed to add subscription. Please try again.';
        alert(errorMessage);
        setAddingSubscription(false);
      }
    } catch (error) {
      console.error('Error adding subscription:', error);
      alert('An error occurred while adding the subscription.');
      setAddingSubscription(false);
    }
  };

  // Generate Stripe dashboard URL
  const getStripeDashboardUrl = () => {
    if (!customerId) return null;

    const baseUrl = 'https://dashboard.stripe.com';
    const path = isBillingDev() ? '/test/customers/' : '/customers/';

    return baseUrl + path + customerId;
  };

  const stripeDashboardUrl = getStripeDashboardUrl();

  return (
    <div className="payment-admin-user-subscriptions">
      {stripeDashboardUrl && (
        <div className="stripe-customer-link">
          <a href={stripeDashboardUrl} target="_blank" rel="noopener noreferrer">
            View in Stripe Dashboard {customerId}
          </a>
        </div>
      )}

      {globalSuccessMessage && (
        <div className='global-success-message'>
          <div className='success-icon'>✓</div>
          <div className='success-content'>
            <div className='success-text'>{globalSuccessMessage}</div>
            <div className='success-subtext'>User data will refresh in a few seconds...</div>
          </div>
        </div>
      )}

      {/* Add Subscription Section */}
      <div className="add-subscription-section">
        {!showAddForm ? (
          <button
            className='btn btn-primary add-subscription-btn'
            onClick={() => setShowAddForm(true)}
          >
            Add Subscription
          </button>
        ) : (
          <div className='add-subscription-form'>
            <h3>Add New Subscription</h3>
                <div className='form-group'>
                  <label>Subscription Type:</label>
                  <select
                    value={selectedSubscription}
                    onChange={(e) => {
                      setSelectedSubscription(e.target.value);
                      setSelectedDevice(''); // Reset device selection when subscription changes
                    }}
                    className='form-control'
                  >
                    <option value="">Select a subscription...</option>
                    {getAvailableSubscriptions().map((group, idx) => (
                      <optgroup key={idx} label={group.label}>
                        {group.options.map((sub) => (
                          <option key={sub.productName} value={sub.productName}>
                            {getSubscriptionDisplayName(sub)}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                {selectedSubscription && requiresDevice(selectedSubscription) && (
                  <div className='form-group'>
                    <label>Device:</label>
                    <select
                      value={selectedDevice}
                      onChange={(e) => setSelectedDevice(e.target.value)}
                      className='form-control'
                    >
                      <option value="">Select a device...</option>
                      {(devices || [])
                        .filter(device => !deviceHasSubscription(device.macAddress, selectedSubscription))
                        .filter(device => !isCellularSubscription(selectedSubscription) || isCellularDevice(device))
                        .filter(device => !isCameraSubscription(selectedSubscription) || canHaveCameraSubscription(device))
                        .map((device) => (
                          <option key={device.macAddress} value={device.macAddress}>
                            {device.name || device.info?.name || 'Unnamed Device'} - {device.macAddress}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                <div className='form-group'>
                  <label>Days:</label>
                  <input
                    type='number'
                    value={addDays}
                    onChange={(e) => setAddDays(e.target.value)}
                    className='form-control'
                    min='1'
                  />
                </div>

                <div className='form-actions'>
                  <button
                    className='btn btn-primary'
                    onClick={submitAddSubscription}
                    disabled={addingSubscription !== false}
                  >
                    {addingSubscription === true ? 'Adding...' :
                      addingSubscription === 'success' ? 'Success!' : 'Add Subscription'}
                  </button>
                  <button
                    className='btn btn-default'
                    onClick={() => {
                      setShowAddForm(false);
                      setSelectedSubscription('');
                      setSelectedDevice('');
                      setAddDays(30);
                    }}
                    disabled={addingSubscription !== false}
                  >
                    Cancel
                  </button>
                </div>
          </div>
        )}
      </div>

      {userSubs.length > 0 && (
        <table className='subscriptions-table'>
          <thead>
            <tr>
              <th>Applies To</th>
              <th>Product</th>
              <th>Billing Period</th>
              <th>Status</th>
              <th>Renews/Expires</th>
              <th>Price</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {userSubs.filter(sub => !/awn_sms/.test(sub.productName)).map((sub, i) => {
              const isAwnPlusSub = /awn-plus-account-(monthly|yearly)/.test(sub.productName);
              const isExtendingSub = extendSubscription && extendSubscription.productName === sub.productName;

              return (
                <tr key={i}>
                  <td>{sub.macAddress || 'Account'}</td>
                  <td>{sub.nickname || sub.productName}</td>
                  <td>{/monthly/.test(sub.productName) ? 'Monthly' : 'Annual'}</td>
                  <td>{subStatusDisplay ? subStatusDisplay(sub) : sub.status}</td>
                  <td>{sub.currentPeriodEnd ? fmtzDate('MMM D, YYYY')(sub.currentPeriodEnd * 1000) : '--'}</td>
                  <td>{displayPriceForSubscription ? displayPriceForSubscription(sub) : '--'}</td>
                  <td>
                      {!isExtendingSub ? (
                        <button 
                          className='btn btn-sm btn-primary extend-btn'
                          onClick={() => toggleExtendSubscription(sub)}
                        >
                          Extend
                        </button>
                      ) : (
                        <div className='extend-subscription-form'>
                          <div className='extend-inputs'>
                                <input
                                  type='number'
                                  value={extendDays}
                                  onChange={handleExtendInputChange}
                                  className='extend-days-input'
                                  min='1'
                                />
                                <span className='days-label'>days</span>
                              </div>
                              <div className='extend-actions'>
                                <button 
                                  className='btn btn-sm btn-primary'
                                  onClick={submitExtendSubscription}
                                  disabled={extendingSubscription !== false}
                                >
                                  {extendingSubscription === true ? 'Extending...' : 
                                    extendingSubscription === 'success' ? 'Success!' : 
                                    extendingSubscription === 'processing' ? 'Processing...' : 'Submit'}
                                </button>
                                <button 
                                  className='btn btn-sm btn-default'
                                  onClick={() => toggleExtendSubscription(null)}
                                  disabled={extendingSubscription !== false}
                                >
                                  Cancel
                                </button>
                              </div>
                        </div>
                      )}
                    </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

AdminUserSubscriptions.propTypes = {
  user: PropTypes.object.isRequired,
  subscriptions: PropTypes.array,
  devices: PropTypes.array,
  onRefreshUser: PropTypes.func
};

export default AdminUserSubscriptions;

AdminUserSubscriptions.displayName = 'AdminUserSubscriptions'
